import { useAddPageMutation } from 'api/siteApi';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Button from 'ui/components/Button';
import { normalizeUrl } from 'utils/proxyUtils';
import { usePermissions } from '../../../hooks/use-permissions';

interface Props {
  addEnabled: boolean;
  siteId: string;
}

export const PageFooter = ({ addEnabled, siteId }: Props) => {
  const { t } = useTranslation();
  const { isPermitted } = usePermissions();
  const [addPage, { isLoading }] = useAddPageMutation();

  // Check if user has permission to add pages for review
  if (!isPermitted("collaborate-sidebar-add-page-for-review")) {
    return null;
  }

  const handleAddPage = () => {
    addPage({
      page_url: normalizeUrl(window.location.href),
      site_id: siteId,
    }).unwrap().then(() => {
      toast.success(t('page_footer.add_success', 'Page added successfully'));
    }).catch((err) => {
      if (err?.data?.message?.includes('Unauthenticated')) {
        toast.error(t('page_footer.add_error_unauthenticated', 'You are not authorized to add pages to this project'));
      } else {
        toast.error(t('page_footer.add_error', 'Failed to add page'));
      }
    });
  };

  return (
    <div className="mt-2 flex flex-col items-center gap-1">
      <Button
        className="w-full"
        loading={isLoading}
        disabled={!addEnabled}
        variant="primary"
        size="sm"
        leftIcon={<CirclePlus size={18} />}
        onClick={handleAddPage}
      >
        {t('page_footer.add_button', 'Add Page for Review')}
      </Button>
      <span className="font-normal text-center pt-1 text-textcolor text-xs break-all">
        {t('page_footer.add_description', 'Add {{title}} to this project', { title: document.title })}
      </span>
    </div>
  );
};